import { useCallback, useEffect, useRef, useState } from 'react';
import { normalizeLocale, type SupportedLocale } from '@evi/shared';
import { VOICE_SAMPLES_VERSION } from '@/lib/voiceSamplesVersion';

/**
 * Plays the EVII voice-picker preview clip for a (locale, persona) pair.
 *
 * The clips are STATIC assets committed under public/voice-samples and
 * generated by scripts/generate-voice-samples.ts — one fixed sentence per
 * pair, so a tap costs a cached file fetch and never a Polly call.
 *
 * Behaviour (Warren, 2026-08-01):
 *   - tapping an option plays immediately
 *   - tapping another stops the first and plays the second
 *   - leaving the screen stops playback
 *   - NO autoplay on open — playback only ever starts from a tap, which
 *     is also what keeps mobile browsers' gesture requirement satisfied
 *
 * Degrades silently: a locale with no clip (Armenian is text-only, and
 * every locale before the clips are generated) simply plays nothing. The
 * picker still works; it just has no audio, exactly as today.
 */
export function useVoicePreview(): {
  play: (locale: string | null | undefined, voice: 'female' | 'male') => void;
  stop: () => void;
  playing: string | null;
} {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);

  const stop = useCallback((): void => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      // Release the element rather than reusing it: a paused <audio> that
      // gets a new src can still fire the previous load's events.
      el.src = '';
      audioRef.current = null;
    }
    setPlaying(null);
  }, []);

  const play = useCallback((
    locale: string | null | undefined,
    voice: 'female' | 'male',
  ): void => {
    // Stop first, unconditionally: tapping a second option must cut the
    // first off rather than overlap it, and re-tapping the same option
    // restarts it from the top.
    stop();
    const loc: SupportedLocale = normalizeLocale(locale);
    const key = `${loc}-${voice}`;
    // ?v=<hash of all clip bytes>. The clips sit at STABLE paths under
    // public/ and are served `immutable, max-age=31536000`, so a browser
    // that fetched an older clip would keep playing it for a YEAR and never
    // revalidate — which is exactly how the pre-fix "Evie" audio survived a
    // correct regenerate AND a correct deploy. Vite does not fingerprint
    // public/ assets, so the version goes in the query string instead.
    const el = new Audio(`/voice-samples/${key}.mp3?v=${VOICE_SAMPLES_VERSION}`);
    audioRef.current = el;
    el.addEventListener('ended', () => {
      if (audioRef.current === el) {
        audioRef.current = null;
        setPlaying(null);
      }
    });
    // A missing clip is not an error the driver should ever see.
    el.addEventListener('error', () => {
      if (audioRef.current === el) {
        audioRef.current = null;
        setPlaying(null);
      }
    });
    setPlaying(key);
    void el.play().catch(() => {
      if (audioRef.current === el) {
        audioRef.current = null;
        setPlaying(null);
      }
    });
  }, [stop]);

  // Leaving the screen stops playback — unmount covers navigation away
  // and the sheet closing alike.
  useEffect(() => stop, [stop]);

  return { play, stop, playing };
}
